// src/pages/dashboards/admin/AdminPayments.jsx
// EFT requests flagged by users ("I've paid"). Two kinds:
//   1. Plan upgrades - confirm the EFT, then activate the tier (12 months).
//   2. Scan packs    - confirm the EFT, then fulfil (allocates scan credits).

import React, { useEffect, useState, useCallback } from "react";
import { listUpgradeIntents, setUserTier, listPackPurchases, fulfilPack, cancelPack } from "../../../api/admin";
import { useToast } from "../../../context/ToastContext";
import { Card, Badge, Button, Loading, EmptyState } from "../../../components/ui";
import { money, date, titleCase } from "../../../lib/format";

const RANK = { free: 0, starter: 1, pro: 2 };

export default function AdminPayments() {
  const toast = useToast();
  const [rows, setRows] = useState(null);
  const [packs, setPacks] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(() => {
    listUpgradeIntents().then(setRows).catch((e) => { toast.error(e.message); setRows([]); });
    listPackPurchases({ status: "pending" }).then(setPacks).catch(() => setPacks([]));
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const activate = async (r) => {
    const tier = r.metadata?.tier;
    if (!tier) return;
    if (!window.confirm(`Activate ${titleCase(tier)} for ${r.email}? Only do this once the EFT has cleared.`)) return;
    setBusyId(r.id);
    try {
      await setUserTier(r.user_id, tier, 12);
      toast.success(`Activated ${titleCase(tier)} for ${r.email}`);
      load();
    } catch (e) { toast.error(e.message); } finally { setBusyId(null); }
  };

  const fulfil = async (p) => {
    if (!window.confirm(`Add ${p.scans} scans to ${p.email}? Only do this once the EFT has cleared.`)) return;
    setBusyId(p.id);
    try {
      await fulfilPack(p.id);
      toast.success(`Added ${p.scans} scans for ${p.email}`);
      load();
    } catch (e) { toast.error(e.message); } finally { setBusyId(null); }
  };

  const cancel = async (p) => {
    if (!window.confirm(`Cancel this scan pack request from ${p.email}?`)) return;
    setBusyId(p.id);
    try {
      await cancelPack(p.id);
      toast.success("Request cancelled");
      load();
    } catch (e) { toast.error(e.message); } finally { setBusyId(null); }
  };

  return (
    <div>
      <h1 className="text-2xl font-display font-bold mb-2">Payments</h1>
      <p className="text-sm text-tb-muted mb-6">
        EFT requests. Confirm the payment landed, then activate the plan or fulfil the scan pack.
      </p>

      {/* plan upgrades */}
      <h2 className="text-sm font-semibold uppercase tracking-wider text-tb-muted mb-3">Plan upgrades</h2>
      <Card className="overflow-hidden mb-10">
        {!rows ? <Loading label="Loading payments" /> : rows.length === 0 ? (
          <EmptyState title="No upgrade requests" hint="When a user taps 'I've paid' on an upgrade, it shows up here." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-tb-muted border-b border-tb-border dark:border-tb-d-border">
                  <th className="px-4 py-3 font-medium">User</th>
                  <th className="px-4 py-3 font-medium">Plan</th>
                  <th className="px-4 py-3 font-medium text-right">Amount</th>
                  <th className="px-4 py-3 font-medium hidden sm:table-cell">Discount</th>
                  <th className="px-4 py-3 font-medium hidden md:table-cell">Requested</th>
                  <th className="px-4 py-3 font-medium text-center">Status</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const m = r.metadata || {};
                  const reqTier = m.tier;
                  const activated =
                    (RANK[r.current_tier] ?? 0) >= (RANK[reqTier] ?? 99) && r.sub_status === "active";
                  return (
                    <tr key={r.id} className="border-b border-tb-border dark:border-tb-d-border last:border-0">
                      <td className="px-4 py-3">
                        <p className="font-medium truncate max-w-[180px]">{r.name || "—"}</p>
                        <p className="text-xs text-tb-muted truncate max-w-[180px]">{r.email}</p>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">{titleCase(reqTier || "—")}</td>
                      <td className="px-4 py-3 text-right tabular-nums font-medium whitespace-nowrap">
                        {m.net != null ? money(m.net) : "—"}
                        {m.discount && m.gross != null && m.gross !== m.net && (
                          <span className="block text-[11px] text-tb-muted line-through">{money(m.gross)}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 hidden sm:table-cell text-tb-muted">{m.discount?.code || "—"}</td>
                      <td className="px-4 py-3 hidden md:table-cell text-tb-muted whitespace-nowrap">{date(r.created_at)}</td>
                      <td className="px-4 py-3 text-center">
                        <Badge tone={activated ? "green" : "amber"}>{activated ? "Activated" : "Awaiting"}</Badge>
                      </td>
                      <td className="px-4 py-3 text-right">
                        {!activated && (
                          <Button onClick={() => activate(r)} disabled={busyId === r.id}>Activate</Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* scan packs */}
      <h2 className="text-sm font-semibold uppercase tracking-wider text-tb-muted mb-3">Scan packs</h2>
      <Card className="overflow-hidden">
        {!packs ? <Loading label="Loading scan packs" /> : packs.length === 0 ? (
          <EmptyState title="No scan pack requests" hint="When a user taps 'I've paid' on a scan pack, it shows up here." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-tb-muted border-b border-tb-border dark:border-tb-d-border">
                  <th className="px-4 py-3 font-medium">User</th>
                  <th className="px-4 py-3 font-medium">Pack</th>
                  <th className="px-4 py-3 font-medium text-right">Amount</th>
                  <th className="px-4 py-3 font-medium hidden sm:table-cell">For</th>
                  <th className="px-4 py-3 font-medium hidden md:table-cell">Requested</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {packs.map((p) => (
                  <tr key={p.id} className="border-b border-tb-border dark:border-tb-d-border last:border-0">
                    <td className="px-4 py-3">
                      <p className="font-medium truncate max-w-[180px]">{p.name || "—"}</p>
                      <p className="text-xs text-tb-muted truncate max-w-[180px]">{p.email}</p>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap font-medium">{p.scans} scans</td>
                    <td className="px-4 py-3 text-right tabular-nums font-medium whitespace-nowrap">{money(p.price)}</td>
                    <td className="px-4 py-3 hidden sm:table-cell text-tb-muted whitespace-nowrap">
                      {p.company_name || "Personal"}
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell text-tb-muted whitespace-nowrap">{date(p.created_at)}</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <div className="inline-flex gap-2">
                        <Button variant="ghost" onClick={() => cancel(p)} disabled={busyId === p.id}>Cancel</Button>
                        <Button onClick={() => fulfil(p)} disabled={busyId === p.id}>Fulfil</Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}